import { CommonModule } from '@angular/common';
import { HttpResponse } from '@angular/common/http';
import { Component, EventEmitter, Inject, Input, NgModule, OnDestroy, OnInit, Output } from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuxiliaryDataFieldType } from '@vendasta/account-group';
// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  FieldSchema,
  ObjectType,
  SchemaService,
  UpdateFieldSchemaRequest,
  UpdateSchemaFieldMask,
  UpdateSchemaFieldMaskOptions,
} from '@vendasta/auxiliary-data-components';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyWrapModule } from '@vendasta/galaxy/galaxy-wrap';
import { SelectInputOptionInterface } from '@vendasta/galaxy/input';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { BehaviorSubject, EMPTY, Observable, Subject, combineLatest } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { DynamicOpenCloseTemplateRefService } from '../dynamic-open-close-template-ref.service';
import { FieldSelectedForEditService } from '../columns/field-selected-for-edit.service';
import { ObjectTypeOption, objectTypeOptions as _objectTypeOptions } from '../object-type-options';
import { CurrencyChoiceComponentModule } from './currency-choice.component';
import { ExternalIdComponentModule } from './external-id/external-id.component';

interface UpdateFieldSchemaStreamResponse {
  updateFieldSchemaResponse: HttpResponse<null>;
  changes: UpdateFieldSchemaRequest;
  fieldMask: UpdateSchemaFieldMask;
}

@Component({
  selector: 'aux-field-editor',
  templateUrl: './field-editor.component.html',
  styleUrls: ['./field-editor.component.scss'],
  standalone: false,
})
export class FieldEditorComponent implements OnInit, OnDestroy {
  createNewField$$ = new BehaviorSubject<boolean>(false);
  @Input() set createNewField(canCreateNewField: boolean) {
    this.createNewField$$.next(canCreateNewField);
  }
  get createNewField(): boolean {
    return this.createNewField$$.value;
  }
  @Input() initialObjectType: ObjectType;
  @Input() showObjectTypeControl = true;
  @Output() changesCompleted = new EventEmitter<ObjectType>();
  @Output() pendingChanges = new EventEmitter<boolean>(false);
  @Output() savingChanges = new EventEmitter<boolean>(false);
  @Output() saveDisabled = new EventEmitter<boolean>(false);
  name = new UntypedFormControl('', Validators.required);
  description = new UntypedFormControl();
  selectedType = new UntypedFormControl(/*TODO validator for picking a non-zero value from dropdown*/);
  externalId = new UntypedFormControl('');
  objectType = new UntypedFormControl(null, Validators.required);
  objectTypeOptions: ObjectTypeOption[] = _objectTypeOptions;
  typeOptions: SelectInputOptionInterface[] = [
    {
      value: AuxiliaryDataFieldType.AUXILIARY_DATA_FIELD_TYPE_STRING,
      label: 'COMMON.AUXILIARY_DATA.TYPES.STRING',
    },
    {
      value: AuxiliaryDataFieldType.AUXILIARY_DATA_FIELD_TYPE_INTEGER,
      label: 'COMMON.AUXILIARY_DATA.TYPES.INTEGER',
    },
    {
      value: AuxiliaryDataFieldType.AUXILIARY_DATA_FIELD_TYPE_DATE,
      label: 'COMMON.AUXILIARY_DATA.TYPES.DATE',
    },
    {
      value: AuxiliaryDataFieldType.AUXILIARY_DATA_FIELD_TYPE_DROPDOWN,
      label: 'COMMON.AUXILIARY_DATA.TYPES.DROPDOWN',
    },
    {
      value: AuxiliaryDataFieldType.AUXILIARY_DATA_FIELD_TYPE_CURRENCY,
      label: 'COMMON.AUXILIARY_DATA.TYPES.CURRENCY',
    },
  ];
  validTypeOptions$: Observable<SelectInputOptionInterface[]> = this.createNewField$$.pipe(
    map((createNewField) => {
      return this.typeOptions.filter((o) => {
        if (!createNewField || this.initialObjectType !== 'business') {
          return true;
        }
        return o.value !== AuxiliaryDataFieldType.AUXILIARY_DATA_FIELD_TYPE_CURRENCY;
      });
    }),
  );

  showDropdownOptions$: Observable<boolean> = this.selectedType.valueChanges.pipe(
    map((selectedType) => selectedType === AuxiliaryDataFieldType.AUXILIARY_DATA_FIELD_TYPE_DROPDOWN),
    startWith(false),
  );
  removeDropdownDisabled$: Observable<boolean>;
  addDropdownDisabled$: Observable<boolean>;
  dropdownOptions = new UntypedFormArray([]);
  dropdownTableRows$$: BehaviorSubject<UntypedFormGroup[]> = new BehaviorSubject<UntypedFormGroup[]>([]);
  dropdownTableColumns: string[] = ['label', 'value', 'create-remove'];
  typeIsCurrency$: Observable<boolean> = this.selectedType.valueChanges.pipe(
    map((selectedType) => selectedType === AuxiliaryDataFieldType.AUXILIARY_DATA_FIELD_TYPE_CURRENCY),
    startWith(false),
  );
  selectedCurrency = new UntypedFormControl(null, Validators.required);

  private selectedFieldId$: Observable<string>;
  private selectedField$: Observable<FieldSchema>;
  private destroyed$$ = new Subject<void>();
  private dropdownOptionsLimit = 200;

  constructor(
    @Inject('PARTNER_ID') readonly partnerId$: Observable<string>,
    private readonly schemaService: SchemaService,
    private readonly selectedFieldService: FieldSelectedForEditService,
    private readonly dynamicOpenCloseService: DynamicOpenCloseTemplateRefService,
    private readonly snackbarService: SnackbarService,
    private readonly translateService: TranslateService,
    private readonly trackingService: ProductAnalyticsService,
  ) {}

  ngOnInit(): void {
    this.objectType.setValue(this.initialObjectType, { emitEvent: false });
    this.selectedFieldId$ = this.selectedFieldService.selectedFieldId$;

    this.selectedField$ = combineLatest([this.partnerId$, this.selectedFieldId$]).pipe(
      switchMap(([partnerId, fieldId]) => {
        return this.schemaService
          .getFieldSchema(this.objectType.value, { fieldId: fieldId, partnerId: partnerId })
          .pipe(map((res) => res.fieldSchema as FieldSchema));
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    // this.selectedField$ = this.selectedFieldId$.pipe(
    //   switchMap((fieldId) => this.schemaService.getFieldSchema(this.objectType.value, fieldId)),
    //   map((res) => res.fieldSchema as FieldSchema),
    //   shareReplay({ bufferSize: 1, refCount: true }),
    // );

    this.selectedField$
      .pipe(
        take(1),
        filter(() => !this.createNewField),
        tap((field) => {
          this.name.setValue(field.fieldName);
          this.description.setValue(field.fieldDescription);
          this.selectedType.setValue(field.fieldType);
          this.externalId.setValue(field.externalId);
          this.dropdownOptions.clear();
          if (field?.dropdownOptions && field.dropdownOptions.length > 0) {
            field.dropdownOptions.forEach((option) => this.addDropdownOption(option.label, option.value));
          } else {
            this.addDropdownOption();
          }
          if (field?.currencyCode) {
            this.selectedCurrency.setValue(field.currencyCode);
          }
        }),
      )
      .subscribe();
    if (this.createNewField) {
      this.name.setValue('');
      this.description.setValue('');
      this.selectedType.setValue(0);
    }
    this.showDropdownOptions$
      .pipe(
        takeUntil(this.destroyed$$),
        tap(() => this.resetDropdownOptions()),
      )
      .subscribe();
    this.removeDropdownDisabled$ = this.dropdownOptions.valueChanges.pipe(
      map((dropdowns: unknown[]) => !dropdowns || dropdowns.length <= 1),
    );
    this.addDropdownDisabled$ = this.dropdownOptions.valueChanges.pipe(
      map((dropdowns: unknown[]) => dropdowns.length >= this.dropdownOptionsLimit),
    );
    this.emitSavingDisabled();
    this.syncDropdownsToTableRows();
    this.emitPendingChanges();
  }

  ngOnDestroy(): void {
    this.destroyed$$.next();
  }

  selectType(type: any): void {
    this.selectedType.setValue(type);
    this.trackingService.trackEvent('CustomFields:field-type-selected', 'CustomFields', 'field-type-selected', 0, {
      objectType: this.objectType.value,
      fieldType: type,
    });
  }

  selectObjectType(type: ObjectType): void {
    this.objectType.setValue(type);
    this.trackingService.trackEvent('CustomFields:object-type-selected', 'CustomFields', 'object-type-selected', 0, {
      objectType: type,
    });
  }

  save(): void {
    if (this.createNewField) {
      this.createNew();
    } else {
      this.updateExisting();
    }
  }

  private resetDropdownOptions(): void {
    this.dropdownOptions.clear();
    this.addDropdownOption();
    this.dropdownOptions.updateValueAndValidity();
  }

  addDropdownOption(label = '', value = ''): void {
    this.dropdownOptions.push(this.getDropdownFormGroup(label, value));
    this.dropdownOptions.updateValueAndValidity();
  }

  private getDropdownFormGroup(label = '', value = ''): UntypedFormGroup {
    return new UntypedFormGroup({
      label: new UntypedFormControl(label, Validators.required),
      value: new UntypedFormControl(value, Validators.required),
    });
  }

  removeDropdownOption(i: number): void {
    this.dropdownOptions.removeAt(i);
    this.dropdownOptions.updateValueAndValidity();
  }

  private syncDropdownsToTableRows(): void {
    this.dropdownOptions.valueChanges
      .pipe(
        takeUntil(this.destroyed$$),
        map((options) => options?.length || 0),
        distinctUntilChanged(),
        tap(() => this.dropdownTableRows$$.next(this.dropdownOptions.controls as UntypedFormGroup[])),
      )
      .subscribe();
    this.addDropdownDisabled$ = this.dropdownTableRows$$.pipe(map((rows) => rows?.length >= this.dropdownOptionsLimit));
  }

  private createNew(): void {
    let newField: FieldSchema = {} as FieldSchema;
    this.partnerId$
      .pipe(
        take(1),
        tap(() => this.savingChanges.emit(true)),
        map((partnerId) => {
          const schema: FieldSchema = {
            partnerId: partnerId,
            fieldType: this.selectedType.value,
            fieldName: this.name.value,
            fieldDescription: this.description.value,
            externalId: this.externalId.value,
          };
          if (this.selectedType.value === AuxiliaryDataFieldType.AUXILIARY_DATA_FIELD_TYPE_DROPDOWN) {
            schema.dropdownOptions = this.dropdownOptions.value;
          }
          if (this.selectedType.value === AuxiliaryDataFieldType.AUXILIARY_DATA_FIELD_TYPE_CURRENCY) {
            schema.currencyCode = this.selectedCurrency.value;
          }
          return schema;
        }),
        tap((schema) => (newField = schema)),
        switchMap((schema) => this.schemaService.createFieldSchema(this.objectType.value, schema)),
        catchError((err) => {
          this.snackbarService.errorSnack(this.translateService.instant('ADMINISTRATION.CUSTOM_FIELDS.SAVE_ERROR'));
          this.trackingService.trackEvent(
            'CustomFields:create-custom-field-fail',
            'CustomFields',
            'create-custom-field-fail',
            0,
            {
              objectType: this.objectType.value,
              fieldType: newField.fieldType,
            },
          );
          console.error('failed to create field', err);
          return EMPTY;
        }),
        tap(() => {
          this.trackingService.trackEvent(
            'CustomFields:create-custom-field-success',
            'CustomFields',
            'create-custom-field-success',
            0,
            {
              objectType: this.objectType.value,
              fieldType: newField.fieldType,
            },
          );
          this.snackbarService.successSnack(this.translateService.instant('ADMINISTRATION.CUSTOM_FIELDS.SAVE_SUCCESS'));
        }),
        tap(() => this.pendingChanges.next(false)),
        tap(() => {
          this.dynamicOpenCloseService.updatePendingChanges(false);
          this.dynamicOpenCloseService.close();
        }),
        tap(() => this.changesCompleted.emit(this.objectType.value)),
        tap(() => this.selectedFieldService.fieldCreated$$.next(newField)),
      )
      .subscribe({
        complete: () => {
          this.savingChanges.emit(false);
        },
      });
  }

  private updateExisting(): void {
    combineLatest([this.partnerId$, this.selectedFieldId$])
      .pipe(
        take(1),
        tap(() => this.savingChanges.emit(true)),
        map(([partnerId, fieldId]) => {
          const changes: UpdateFieldSchemaRequest = {
            partnerId: partnerId,
            fieldId: fieldId,
            fieldType: this.selectedType.value,
            fieldName: this.name.value,
            fieldDescription: this.description.value,
            externalId: this.externalId.value,
          };
          const fieldMask: UpdateSchemaFieldMask = [
            UpdateSchemaFieldMaskOptions.name,
            UpdateSchemaFieldMaskOptions.description,
            UpdateSchemaFieldMaskOptions.externalId,
          ];
          if (this.selectedType.value === AuxiliaryDataFieldType.AUXILIARY_DATA_FIELD_TYPE_DROPDOWN) {
            changes.dropdownOptions = this.dropdownOptions.value;
            fieldMask.push(UpdateSchemaFieldMaskOptions.dropdownOptions);
          }
          if (this.selectedType.value === AuxiliaryDataFieldType.AUXILIARY_DATA_FIELD_TYPE_CURRENCY) {
            changes.currencyCode = this.selectedCurrency.value;
            fieldMask.push(UpdateSchemaFieldMaskOptions.currencyCode);
          }
          return { changes, fieldMask };
        }),
        switchMap(({ changes, fieldMask }) =>
          this.schemaService.updateFieldSchema(this.objectType.value, changes, fieldMask).pipe(
            map((updateFieldSchemaResponse) => ({
              updateFieldSchemaResponse,
              changes,
              fieldMask,
            })),
          ),
        ),
        catchError((err) => {
          this.snackbarService.errorSnack(this.translateService.instant('ADMINISTRATION.CUSTOM_FIELDS.EDIT_ERROR'));
          this.trackingService.trackEvent(
            'CustomFields:edit-custom-field-fail',
            'CustomFields',
            'edit-custom-field-fail',
            0,
            {
              objectType: this.objectType.value,
            },
          );
          console.error('failed to update field', err);
          return EMPTY;
        }),
        tap((response: UpdateFieldSchemaStreamResponse) => {
          this.snackbarService.successSnack(this.translateService.instant('ADMINISTRATION.CUSTOM_FIELDS.EDIT_SUCCESS'));
          this.trackingService.trackEvent(
            'CustomFields:edit-custom-field-success',
            'CustomFields',
            'edit-custom-field-success',
            0,
            {
              objectType: this.objectType.value,
              fieldType: response.changes.fieldType,
            },
          );
        }),
        tap(() => this.pendingChanges.next(false)),
        tap(() => {
          this.dynamicOpenCloseService.updatePendingChanges(false);
          this.dynamicOpenCloseService.close();
        }),
        tap(() => this.changesCompleted.emit(this.objectType.value)),
      )
      .subscribe({
        complete: () => {
          this.savingChanges.emit(false);
        },
      });
  }

  cancel(): void {
    const posthogEventProperties = {
      objectType: this.objectType.value,
    };
    if (this.selectedType.value) {
      posthogEventProperties['fieldType'] = this.selectedType.value;
    }

    if (this.createNewField) {
      this.trackingService.trackEvent(
        'CustomFields:create-custom-field-cancel',
        'CustomFields',
        'create-custom-field-cancel',
        0,
        posthogEventProperties,
      );
    } else {
      this.trackingService.trackEvent(
        'CustomFields:edit-custom-field-cancel',
        'CustomFields',
        'edit-custom-field-cancel',
        0,
        posthogEventProperties,
      );
    }
    this.dynamicOpenCloseService.close();
  }

  private emitSavingDisabled(): void {
    combineLatest([
      this.savingChanges.asObservable().pipe(startWith(false)),
      this.name.valueChanges.pipe(startWith(undefined)),
      this.description.valueChanges.pipe(startWith(undefined)),
      this.selectedType.valueChanges.pipe(startWith(undefined)),
      this.showDropdownOptions$,
      this.dropdownOptions.valueChanges.pipe(startWith(undefined)),
      this.typeIsCurrency$,
      this.selectedCurrency.valueChanges.pipe(startWith(undefined)),
    ])
      .pipe(
        takeUntil(this.destroyed$$),
        map(
          ([saving, , , , typeIsDropdown, , typeIsCurrency]) =>
            saving ||
            this.name.invalid ||
            this.description.invalid ||
            this.selectedType.invalid ||
            (typeIsDropdown && this.dropdownOptions.invalid) ||
            (typeIsCurrency && this.selectedCurrency.invalid),
        ),
        startWith(this.createNewField),
        tap((saveDisabled) => {
          this.saveDisabled.emit(saveDisabled);
        }),
      )
      .subscribe();
  }

  private emitPendingChanges(): void {
    combineLatest([
      this.name.valueChanges,
      this.description.valueChanges,
      this.selectedType.valueChanges,
      this.showDropdownOptions$,
      this.dropdownOptions.valueChanges,
    ])
      .pipe(
        takeUntil(this.destroyed$$),
        map(
          ([, , , typeIsDropdown]) =>
            this.name.dirty ||
            this.description.dirty ||
            this.selectedType.dirty ||
            (typeIsDropdown && this.dropdownOptions.dirty),
        ),
        tap((p) => this.pendingChanges.next(p)),
        tap((p) => this.dynamicOpenCloseService.updatePendingChanges(p)),
      )
      .subscribe();
  }
}

@NgModule({
  exports: [FieldEditorComponent],
  declarations: [FieldEditorComponent],
  imports: [
    CommonModule,
    CurrencyChoiceComponentModule,
    ExternalIdComponentModule,
    GalaxyWrapModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatTableModule,
    MatTooltipModule,
    ReactiveFormsModule,
    TranslateModule,
    GalaxyAlertModule,
    GalaxyFormFieldModule,
  ],
  providers: [SchemaService],
})
export class FieldEditorComponentModule {}
